
import { defineComponent, PropType } from "vue";
import store from "@/store";

import {
    IPiarModel,
    IPiarAdjustmentsModel,
    ISystemSubjectsModel,
    IStudentsModel,
    IIndividualEnrollmentModel
} from "@/interfaces/modules";

import PiarService from "@/api/Piar/PiarService";
import PiarAdjustmentsService from "@/api/Piar/PiarAdjustmentsService";
import SystemSubjectsService from "@/api/Academic/SystemSubjectsService";
import StudentsService from "@/api/Aula/StudentsService";
import IndividualEnrollmentService from "@/api/Enrollment/IndividualEnrollmentService";

export default defineComponent({
    name: "PiarAdjustmentsTab",
    props: {
        piarId: {
            type: Number as PropType<number>,
            required: true,
        },
    },
    data() {
        return {
            header: {} as Partial<IPiarModel> & {
                StudentName?: string;
                CourseGroupName?: string;
                AcademicYearName?: string;
                PeriodName?: string;
            },
            previewUrl: new URL("@/assets/images/user/sin_imagen.png", import.meta.url).href,

            loadingHeader: false,
            loadingAdjustments: false,
            loadingSubjects: false,
            savingNew: false,

            students: [] as IStudentsModel[],
            adjustments: [] as IPiarAdjustmentsModel[],
            subjects: [] as ISystemSubjectsModel[],

            newAdjustment: {
                PiarId: 0,
                SubjectId: null as number | null,
                ObjectiveStandard: "",
                ObjectiveAdjusted: "",
                BarrierType: "",
                BarrierDescription: "",
                ReasonableAdjustment: "",
                Competence: "",
                Status: 1,
            } as any,

            barrierTypeOptions: [
                { value: "", text: "Seleccione el tipo de barrera" },
                { value: "ACADÉMICA", text: "Académica" },
                { value: "SENSORIAL", text: "Sensorial" },
                { value: "MOTORA", text: "Motora" },
                { value: "COGNITIVA", text: "Cognitiva" },
                { value: "CONDUCTUAL", text: "Conductual" },
                { value: "OTRA", text: "Otra" },
            ],
        };
    },
    computed: {
        activeAdjustmentsCount(): number {
            return this.adjustments.filter((a) => a.Status).length;
        },
        lastUpdateDisplay(): string {
            const dates = this.adjustments
                .map((a) => a.ModifiedDate || a.CreatedDate)
                .filter((d) => !!d)
                .map((d) => new Date(d as any));

            if (!dates.length) return "Sin registros";

            const last = dates.sort((a, b) => b.getTime() - a.getTime())[0];
            return last.toLocaleDateString();
        },
    },
    async mounted() {
        this.students = await StudentsService.GetStudents();
        this.newAdjustment.PiarId = this.piarId;
        await Promise.all([this.loadHeader(), this.loadSubjects()]);
        await this.loadAdjustments();
    },
    methods: {
        async loadHeader() {
            this.loadingHeader = true;
            try {
                const res = await PiarService.GetById(this.piarId);
                this.header = res || {};
                let enrollment = await IndividualEnrollmentService.GetByStudentLastEnrollment(res.StudentId as any) as IIndividualEnrollmentModel;
                this.header.AcademicYearName = enrollment?.AcademicYearName;
                this.header.CourseGroupName = enrollment?.CourseGroupName;
                this.header.StudentName = enrollment?.StudentName;
                if (this.header) {
                    const _student = this.students.find((f) => f.Id === this.header.StudentId);
                    this.previewUrl = (_student?.Image as any) || this.previewUrl;
                }
            } finally {
                this.loadingHeader = false;
            }
        },
        async loadSubjects() {
            this.loadingSubjects = true;
            try {
                this.subjects = await SystemSubjectsService.GetSubjects();
            } finally {
                this.loadingSubjects = false;
            }
        },
        async loadAdjustments() {
            this.loadingAdjustments = true;
            try {
                const list = await PiarAdjustmentsService.GetByPiarId(this.piarId);

                const subjectsMap = new Map<number, string>(
                    this.subjects.map((s) => [s.Id, `${s.Code} - ${s.Name}`])
                );

                this.adjustments = (list || []).map((a: any) => ({
                    ...a,
                    SubjectName: a.SubjectName || subjectsMap.get(a.SubjectId) || "",
                }));
            } finally {
                this.loadingAdjustments = false;
            }
        },
        formatBarrierType(value: string | null | undefined): string {
            if (!value) return "Tipo de barrera no especificado";
            switch (value.toUpperCase()) {
                case "ACADÉMICA":
                    return "Barrera académica";
                case "SENSORIAL":
                    return "Barrera sensorial";
                case "MOTORA":
                    return "Barrera motora";
                case "COGNITIVA":
                    return "Barrera cognitiva";
                case "CONDUCTUAL":
                    return "Barrera conductual";
                case "OTRA":
                    return "Otra barrera";
                default:
                    return value;
            }
        },
        resetNewForm() {
            this.newAdjustment = {
                PiarId: this.piarId,
                SubjectId: null,
                ObjectiveStandard: "",
                ObjectiveAdjusted: "",
                BarrierType: "",
                BarrierDescription: "",
                ReasonableAdjustment: "",
                Competence: "",
                Status: true,
            };
        },
        async saveNewAdjustment() {
            try {
                this.savingNew = true;

                await PiarAdjustmentsService.Post(this.newAdjustment as IPiarAdjustmentsModel);

                store.dispatch("core/popupDialog", {
                    text: "Ajuste guardado con éxito.",
                    type: "success",
                    showCancelButton: false,
                });

                this.resetNewForm();
                await this.loadAdjustments();
            } catch (err: any) {
                store.dispatch("core/popupDialog", {
                    text: `Error: ${err?.response?.data?.message || err?.message}`,
                    type: "error",
                    showCancelButton: false,
                });
            } finally {
                this.savingNew = false;
            }
        },
    },
});
